import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import '../../styles/keywords-search.scss'
import { Button } from 'antd'
import { fetchRecommendSearchKeywords } from '../../../../../actions/photo'
import { useAppDispatch, useAppSelector } from '../../../../../app/hooks'
import TagsInputDropdownForKeywords from './TagsInputDropdownForKeywords'
import TagsInputWithIcon from './TagsInputWithIcon'
import { useSearchHistory } from '../../../../../app/hooks/useSearchKeywordsHistory'
import {
  useWebSearchFilterHistory,
  useWebSearchKeywordFilterHistory
} from '../../../../../app/hooks/useWebSearchFilterHistory'
import { FilterState } from '../../types'
import dayjs from 'dayjs'
import { useNlpKeywordSubmit } from '../../hooks/useNlpKeywordSubmit'
import { selectUiFilter } from '../../../../../slices/photoFilterSlice'

const ACCEPTANCE_SEARCHED_KEY = 'acceptanceSearched'
const CONSTRUCTION_INDUSTRY_CODE = ['020010', '020020', '020030']
const SEARCH_GUIDE_SHOWN_KEY = 'searchGuideShown'
const SEARCH_GUIDE_DELAY = 3000 // 页面出现3秒后展示
const SEARCH_GUIDE_DURATION = 5000 // 引导条展示5秒

export type RecommendKeywordItem = {
  word: string
  type: 'history' | 'recommend'
  isNlp?: boolean
  /** 与 localStorage 中 isSessionGenerated 一致：仅筛选拼装历史为 true */
  isSessionGenerated?: boolean
}

export type RecommendSlotPayload = {
  keywords: RecommendKeywordItem[]
  onSelect: (item: RecommendKeywordItem) => void
}

interface KeywordsSearchProps {
  value: string[]
  onChange: (value: string[]) => void
  onSensorOfClickEvent: (
    clickItem: string,
    restProperties?: Record<string, any>
  ) => void
  // 支持 options 参数：{ preserveInput?: boolean }
  onSubmit: (
    latestKeywords?: string[],
    overrideFilter?: Partial<FilterState>,
    options?: { preserveInput?: boolean }
  ) => void
  onInputFocusChange?: (isFocused: boolean) => void
  onInputChange?: (text: string) => void
  recommendPlacement?: 'external'
  onRecommendChange?: (payload: RecommendSlotPayload) => void
  showLabel?: boolean
  label?: React.ReactNode
  showTagsDropdown?: boolean
  resetToken?: number
  // AI引导词结构
  aiGuideStructured?: {
    departmentUsers?: any[]
    departments?: any[]
    totalMembersCount?: number
    time?: { start?: any; end?: any }
  } | null
  /** 搜索按钮右侧扩展操作（例如"返回按人员结果"） */
  extraAction?: React.ReactNode
}

const KeywordsSearch: React.FC<KeywordsSearchProps> = ({
  value = [],
  onChange = () => {
    /* noop */
  },
  onSensorOfClickEvent = () => {
    /* noop */
  },
  onSubmit = () => {
    /* noop */
  },
  onInputFocusChange = () => {
    /* noop */
  },
  onInputChange = () => {
    /* noop */
  },
  recommendPlacement,
  onRecommendChange,
  showLabel = true,
  label = '筛选内容',
  showTagsDropdown = true,
  resetToken,
  aiGuideStructured = null,
  extraAction
}) => {
  const dispatch = useAppDispatch()
  const groupID = useAppSelector((state) => state.group.groupId)
  const industryCode = useAppSelector((state) => state.group.industryCode)
  const uiFilter = useAppSelector(selectUiFilter)
  const enableNlp = uiFilter?.groupByUser === false

  const { history: filterHistory } = useWebSearchFilterHistory()
  const { history: keywordFilterHistory } = useWebSearchKeywordFilterHistory()
  const [recommendKeywords, setRecommendKeywords] = useState<string[]>([])
  const [inputValue, setInputValue] = useState('')
  const [dropdownSuppressToken, setDropdownSuppressToken] = useState(0)
  const [isInputFocused, setIsInputFocused] = useState(false)
  const [guideBarVisible, setGuideBarVisible] = useState(false)
  const inputValueRef = useRef('') // 使用 ref 存储最新的输入值，避免状态异步更新导致的问题
  const { history: searchHistory = [], addKeywords } = useSearchHistory()
  const lastResetTokenRef = useRef<number | null>(null)

  // 当 value 从外部变化为 [] 时，同步清空 input（无 tag 但输入框有内容时保留：NLP 一句话只在 input）
  useEffect(() => {
    if (value.length === 0) {
      if (inputValue.trim() !== '') {
        return
      }
      setInputValue('')
      inputValueRef.current = ''
      onInputChange('')
    }
  }, [value, inputValue, onInputChange])

  useEffect(() => {
    if (resetToken === undefined) return
    if (lastResetTokenRef.current === null) {
      lastResetTokenRef.current = resetToken
      return
    }
    if (lastResetTokenRef.current === resetToken) return
    lastResetTokenRef.current = resetToken
    setInputValue('')
    inputValueRef.current = ''
    onInputChange('')
  }, [resetToken])

  const handleFetchRecommendKeywords = useCallback(() => {
    dispatch(
      fetchRecommendSearchKeywords({
        groupID
      })
    ).then((data: any) => {
      if (data.status === 0) {
        const searchRecommend = data.searchRecommend.slice(0, 3)
        const acceptanceSearched = localStorage.getItem(ACCEPTANCE_SEARCHED_KEY)
        if (
          CONSTRUCTION_INDUSTRY_CODE.indexOf(industryCode ?? '') > -1 &&
          searchRecommend.length > 0 &&
          !acceptanceSearched
        ) {
          if (searchRecommend.indexOf('验收') === -1) {
            searchRecommend.splice(0, 0, '验收')
          }
        }
        setRecommendKeywords(searchRecommend.slice(0, 3))
        onSensorOfClickEvent('enterPage', {
          historyRecordNum: searchHistory.length,
          recommendNum: searchRecommend.slice(0, 3).length
        })
      }
    })
  }, [groupID, industryCode, searchHistory, dispatch])

  const onValueChange = useCallback(
    (keywords: string[], opts?: { keepInput?: boolean }) => {
      onChange(keywords)
      if (!opts?.keepInput) {
        // 外部更新 tags 时（如点击推荐词），清空正在输入的文本
        setInputValue('')
        inputValueRef.current = ''
        onInputChange('')
      }
    },
    [onChange, onInputChange]
  )

  const { handleSearch, submitWithNonNlp } = useNlpKeywordSubmit({
    groupID,
    enableNlp,
    onSubmit,
    onValueChange,
    addKeywords,
    inputValueRef,
    setDropdownSuppressToken,
    onInputChange: (text: string) => {
      setInputValue(text)
      inputValueRef.current = text
      onInputChange(text)
    }
  })

  const recommendKeywordsWithHistory: RecommendKeywordItem[] = useMemo(() => {
    const filterHistoryWordsWithTime: Array<
      RecommendKeywordItem & { createdAt: number }
    > = (filterHistory || []).map((item) => ({
      word: item.text,
      type: 'history' as const,
      isNlp: true,
      createdAt: Number(item.createdAt) || 0,
      isSessionGenerated: item.isSessionGenerated === true
    }))

    const keywordFilterHistoryWordsWithTime: Array<
      RecommendKeywordItem & { createdAt: number }
    > = (keywordFilterHistory || []).map((item) => ({
      word: item.text,
      type: 'history' as const,
      isNlp: true,
      createdAt: Number(item.createdAt) || 0,
      isSessionGenerated: item.isSessionGenerated === true
    }))

    const historyWordsWithTime: Array<
      RecommendKeywordItem & { createdAt: number }
    > = (searchHistory || []).map((item) => ({
      word: item.word,
      isNlp: item.isNlp,
      type: 'history' as const,
      createdAt: Number((item as any).createdAt) || 0,
      isSessionGenerated: item.isSessionGenerated === true
    }))

    const historyMerged = [
      ...historyWordsWithTime,
      ...filterHistoryWordsWithTime,
      ...keywordFilterHistoryWordsWithTime
    ].sort((a, b) => b.createdAt - a.createdAt)

    const dedupedHistory: RecommendKeywordItem[] = []
    const seenHistory = new Set<string>()
    historyMerged.forEach((item) => {
      if (seenHistory.has(item.word)) return
      seenHistory.add(item.word)
      dedupedHistory.push(item)
    })

    const hasSearchHistory = Boolean(searchHistory?.length)

    // 如果没有关键词历史，只展示筛选组合历史 + 接口推荐
    const recommendWords: RecommendKeywordItem[] = recommendKeywords
      .filter((item) => {
        const inKeywordHistory = (searchHistory || []).some(
          (h) => h.word === item
        )
        const inFilterHistory = (filterHistory || []).some(
          (h) => h.text === item
        )
        const inKeywordFilterHistory = (keywordFilterHistory || []).some(
          (h) => h.text === item
        )
        return !inKeywordHistory && !inFilterHistory && !inKeywordFilterHistory
      })
      .map((item) => ({
        word: item,
        type: 'recommend' as const
      }))

    // “按时间倒序合并历史，再拼接口推荐”
    // recommendWords 始终放在历史之后，避免把刚输入的历史项挤出去。
    if (!hasSearchHistory && dedupedHistory.length === 0) {
      return recommendWords
    }

    return [...dedupedHistory, ...recommendWords]
  }, [filterHistory, keywordFilterHistory, searchHistory, recommendKeywords])

  const handleRecommendItemClick = useCallback(
    (item: RecommendKeywordItem) => {
      onSensorOfClickEvent('recommendSearch', {
        clickSearchContent: item,
        bool1: item.type === 'history',
        bool4: item.isSessionGenerated === true
      })
      setDropdownSuppressToken((token) => token + 1)
      if (item.isNlp === false) {
        // 非NLP搜索：追加到现有tags，不覆盖
        const currentTags = [...value] // 获取当前所有tags
        if (!currentTags.includes(item.word)) {
          // 如果当前长度是3，则把最后一个替换掉，总长度不能超过3
          if (currentTags.length < 3) {
            currentTags.push(item.word)
          } else {
            currentTags.splice(2, 1, item.word)
          }
        } else {
          return
        }
        onValueChange(currentTags)
        submitWithNonNlp(currentTags)
      } else {
        // NLP 搜索：关键词放在输入框，不生成 tag
        setInputValue(item.word)
        inputValueRef.current = item.word
        onInputChange(item.word)
        onValueChange([], { keepInput: true })
        handleSearch([])
      }
    },
    [value, onValueChange, onSensorOfClickEvent, handleSearch, submitWithNonNlp]
  )

  const handleRecommendItemClickRef = useRef(handleRecommendItemClick)
  useEffect(() => {
    handleRecommendItemClickRef.current = handleRecommendItemClick
  }, [handleRecommendItemClick])

  // 提供给外部推荐区域使用的选择回调
  const externalRecommendSelect = useCallback((item: RecommendKeywordItem) => {
    handleRecommendItemClickRef.current(item)
  }, [])

  const lastExternalKeywordsKey = useRef<string | null>(null)

  useEffect(() => {
    if (recommendPlacement !== 'external' || !onRecommendChange) return
    const key = JSON.stringify(recommendKeywordsWithHistory)
    if (lastExternalKeywordsKey.current === key) return
    lastExternalKeywordsKey.current = key
    onRecommendChange({
      keywords: recommendKeywordsWithHistory,
      onSelect: externalRecommendSelect
    })
  }, [
    recommendKeywordsWithHistory,
    recommendPlacement,
    onRecommendChange,
    externalRecommendSelect
  ])

  useEffect(() => {
    handleFetchRecommendKeywords()
  }, [handleFetchRecommendKeywords])

  // 构建AI输入框引导词（支持 部门+成员 与 跨年时间 格式）
  const aiPlaceholder = useMemo(() => {
    if (!aiGuideStructured) return null
    const parts: string[] = []
    const departments = aiGuideStructured.departments || []
    const users = aiGuideStructured.departmentUsers || []
    const hasDepartments = departments.length > 0
    const hasUsers = users.length > 0

    // 如果同时存在部门和成员：显示 "部门名、成员名等N人"
    if (hasDepartments && hasUsers) {
      const dept = departments[0]
      const deptName = typeof dept === 'string' ? dept : dept.name || dept.title
      const firstUser = users[0]
      const firstUserName =
        firstUser && (firstUser.name || firstUser.userID || firstUser)
      const total = aiGuideStructured.totalMembersCount + dept.num
      const memberText = `${deptName}${
        firstUserName ? '、' + firstUserName : ''
      }等${total}人`
      parts.push(memberText)
    } else if (hasDepartments) {
      // 如果部门就一个就用现在这个逻辑，如果有多个部门，最多展示两个部门名称，剩下的用等N人代替
      if (departments.length > 1) {
        const deptNames = departments.map((dept) => dept.name || dept.title)
        const deptName = deptNames.slice(0, 2).join('、')
        const total = departments.reduce((acc, dept) => acc + dept.num, 0)
        const memberText = `${deptName}等${total}人`
        parts.push(memberText)
      } else {
        const dept = departments[0]
        const deptName =
          typeof dept === 'string' ? dept : dept.name || dept.title
        if (deptName) parts.push(deptName)
      }
    } else if (hasUsers) {
      const displayNames = users
        .slice(0, 2)
        .map((u: any) => u.name || u.userID || u)
      let memberText = displayNames.join('、')
      if (
        (aiGuideStructured.totalMembersCount || users.length) >
        displayNames.length
      ) {
        const total = aiGuideStructured.totalMembersCount || users.length
        memberText = `${memberText}等${total}人`
      }
      parts.push(memberText)
    }

    if (
      aiGuideStructured.time &&
      aiGuideStructured.time.start &&
      aiGuideStructured.time.end
    ) {
      const start = aiGuideStructured.time.start
      const end = aiGuideStructured.time.end
      const currentYear = dayjs().year()
      let timeText = ''
      if (start.isSame(end, 'day')) {
        timeText =
          start.year() === currentYear
            ? start.format('M月D日')
            : start.format('YYYY年M月D日')
      } else {
        // 若跨年或任一端不是当年，则显示完整年份
        const needYear =
          start.year() !== end.year() ||
          start.year() !== currentYear ||
          end.year() !== currentYear
        if (needYear) {
          timeText = `${start.format('YYYY年M月D日')}-${end.format(
            'YYYY年M月D日'
          )}`
        } else {
          timeText = `${start.format('M月D日')}-${end.format('M月D日')}`
        }
      }
      parts.push(timeText)
    }

    if (parts.length === 0) return null
    return `${parts.join('')}拍的`
  }, [aiGuideStructured])

  // AI下拉推荐款引导
  const aiSuggestion = useMemo(() => {
    if (!aiGuideStructured) return null
    const base = aiPlaceholder || ''
    const text = inputValue ? `${base}${inputValue}` : `${base}`
    return {
      text,
      structured: aiGuideStructured
    }
  }, [aiGuideStructured, aiPlaceholder, inputValue])

  // 引导条显示逻辑：页面出现3秒后展示，展示5秒后自动关闭，之后不再显示
  useEffect(() => {
    const hasShown = localStorage.getItem(SEARCH_GUIDE_SHOWN_KEY)
    if (hasShown === 'true') {
      return
    }

    let durationTimer: ReturnType<typeof setTimeout> | null = null

    // 页面出现3秒后显示引导条
    const delayTimer = setTimeout(() => {
      setGuideBarVisible(true)

      // 引导条展示5秒后自动关闭
      durationTimer = setTimeout(() => {
        setGuideBarVisible(false)
        localStorage.setItem(SEARCH_GUIDE_SHOWN_KEY, 'true')
      }, SEARCH_GUIDE_DURATION)
    }, SEARCH_GUIDE_DELAY)

    return () => {
      clearTimeout(delayTimer)
      if (durationTimer) {
        clearTimeout(durationTimer)
      }
    }
  }, [])

  return (
    <div className="keywords-wrapper keywords-wrapper--simple">
      {showLabel && <p className="label">{label}</p>}
      <div
        className={`keywords-input-wrapper${
          extraAction ? ' keywords-input-wrapper--with-extra' : ''
        }`}
      >
        <TagsInputWithIcon
          tags={value}
          inputValue={inputValue}
          onChange={onValueChange}
          onInputChange={(nextInput) => {
            setInputValue(nextInput)
            inputValueRef.current = nextInput
            onInputChange(nextInput)
          }}
          onSensorOfClickEvent={onSensorOfClickEvent}
          onFocusChange={(focused) => {
            setIsInputFocused(focused)
            onInputFocusChange(focused)
          }}
          onSubmit={(latestTags, _overrideFilter, options) =>
            handleSearch(latestTags, options)
          }
          aiPlaceholder={aiPlaceholder}
        />
        <div className="keywords-actions">
          <Button
            type="primary"
            className="keywords-search-btn"
            onClick={() =>
              handleSearch(
                value,
                value.length === 0 ? { preserveInput: true } : undefined
              )
            }
          >
            搜索
          </Button>
          {extraAction}
        </div>
      </div>
      {/* 引导条 */}
      {guideBarVisible && (
        <div className="search-guide-bar">
          <div className="guide-bar-content">
            <span className="guide-bar-text">试试用一句话搜索</span>
          </div>
        </div>
      )}
      {showTagsDropdown && (
        <TagsInputDropdownForKeywords
          tags={value}
          input={inputValue}
          onSubmitTag={onValueChange}
          onSensorOfClickEvent={onSensorOfClickEvent}
          onSubmit={submitWithNonNlp}
          onNlpSubmit={handleSearch}
          onInputChange={(text: string) => {
            setInputValue(text)
            inputValueRef.current = text
            onInputChange(text)
          }}
          suppressTriggerToken={dropdownSuppressToken}
          inputFocused={isInputFocused}
          aiSuggestion={aiSuggestion}
        />
      )}
    </div>
  )
}

export default KeywordsSearch
